import { Container, Graphics, Text, TextStyle, Sprite, Assets, Texture } from 'pixi.js';
import { COLORS, FONTS, LAYOUT, SPACING } from '../constants/theme';
import { QRService } from '@services/QRService';
import type { Player } from '@/types';

export interface PlayerResultCardOptions {
  player: Player;
  ratingDelta: number;
  isAI: boolean;
  width?: number;
  height?: number;
}

const DEFAULT_CARD_WIDTH = 280;
const DEFAULT_CARD_HEIGHT = 380;

// Layout zones as proportions of card height
const LAYOUT_ZONES = {
  NAME_Y: 0.04,
  PORTRAITS_Y: 0.12,
  RATING_Y: 0.32,
  DELTA_Y: 0.46,
  QR_Y: 0.56,
};

/**
 * Card component displaying player results on the game over screen
 * Shows: player name, 3 warrior portraits, rating (large), rating delta, QR code
 */
export class PlayerResultCard extends Container {
  private options: PlayerResultCardOptions;
  private background: Graphics;
  private nameText: Text;
  private ratingText: Text;
  private deltaText: Text;
  private warriorPortraits: Container;
  private qrContainer: Container;

  constructor(options: PlayerResultCardOptions) {
    super();
    this.options = options;

    const width = options.width ?? DEFAULT_CARD_WIDTH;
    const height = options.height ?? DEFAULT_CARD_HEIGHT;

    // Calculate proportional positions
    const nameY = height * LAYOUT_ZONES.NAME_Y;
    const portraitsY = height * LAYOUT_ZONES.PORTRAITS_Y;
    const ratingY = height * LAYOUT_ZONES.RATING_Y;
    const deltaY = height * LAYOUT_ZONES.DELTA_Y;
    const qrY = height * LAYOUT_ZONES.QR_Y;

    // Calculate portrait size proportionally
    const portraitSize = Math.min(width * 0.2, height * 0.15);

    // Background
    this.background = new Graphics();
    const playerColor = options.player.id === 'player1' ? COLORS.PLAYER1 : COLORS.PLAYER2;
    this.background.roundRect(0, 0, width, height, LAYOUT.BORDER_RADIUS);
    this.background.fill({ color: COLORS.BG_SECONDARY, alpha: 0.95 });
    this.background.stroke({ color: playerColor, width: 3 });
    this.addChild(this.background);

    // Player name
    const nameStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.LG,
      fontWeight: 'bold',
      fill: playerColor,
    });
    this.nameText = new Text({ text: options.player.name, style: nameStyle });
    this.nameText.anchor.set(0.5, 0);
    this.nameText.x = width / 2;
    this.nameText.y = nameY;
    this.addChild(this.nameText);

    // Warrior portraits container
    this.warriorPortraits = new Container();
    this.warriorPortraits.y = portraitsY;
    this.addChild(this.warriorPortraits);
    this.createWarriorPortraits(width, portraitSize);

    // Rating (large)
    const ratingStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.TITLE,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    this.ratingText = new Text({
      text: options.player.rating.toString(),
      style: ratingStyle,
    });
    this.ratingText.anchor.set(0.5, 0);
    this.ratingText.x = width / 2;
    this.ratingText.y = ratingY;
    this.addChild(this.ratingText);

    // Rating delta
    const deltaColor = options.ratingDelta >= 0 ? COLORS.SUCCESS : COLORS.DANGER;
    const deltaPrefix = options.ratingDelta >= 0 ? '+' : '';
    const deltaStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.XL,
      fontWeight: 'bold',
      fill: deltaColor,
    });
    this.deltaText = new Text({
      text: `(${deltaPrefix}${options.ratingDelta})`,
      style: deltaStyle,
    });
    this.deltaText.anchor.set(0.5, 0);
    this.deltaText.x = width / 2;
    this.deltaText.y = deltaY;
    this.addChild(this.deltaText);

    // QR Code or AI badge
    this.qrContainer = new Container();
    this.qrContainer.y = qrY;
    this.addChild(this.qrContainer);

    if (options.isAI) {
      this.createAIBadge(width, height);
    } else {
      this.createQRCode(width, height);
    }
  }

  private createWarriorPortraits(cardWidth: number, portraitSize: number): void {
    const warriors = this.options.player.warriors;
    const totalWidth = warriors.length * portraitSize + (warriors.length - 1) * SPACING.SM;
    const startX = (cardWidth - totalWidth) / 2;

    warriors.forEach((warrior, index) => {
      const portraitContainer = new Container();
      portraitContainer.x = startX + index * (portraitSize + SPACING.SM);

      // Portrait background/border
      const border = new Graphics();
      border.roundRect(0, 0, portraitSize, portraitSize, 6);
      border.fill({ color: COLORS.BG_TERTIARY });
      border.stroke({ color: COLORS.TEXT_MUTED, width: 2 });
      portraitContainer.addChild(border);

      // Load and display sprite image
      const spriteUrl = warrior.definition.images.sprite;
      Assets.load(spriteUrl).then((texture) => {
        const sprite = new Sprite(texture);
        sprite.width = portraitSize - SPACING.SM;
        sprite.height = portraitSize - SPACING.SM;
        sprite.x = SPACING.XS;
        sprite.y = SPACING.XS;
        portraitContainer.addChild(sprite);
      }).catch(() => {
        // Fallback: show first letter
        const initial = new Text({
          text: warrior.definition.name.charAt(0).toUpperCase(),
          style: {
            fontFamily: FONTS.PRIMARY,
            fontSize: FONTS.SIZES.LG,
            fill: COLORS.TEXT_PRIMARY,
          },
        });
        initial.anchor.set(0.5);
        initial.x = portraitSize / 2;
        initial.y = portraitSize / 2;
        portraitContainer.addChild(initial);
      });

      this.warriorPortraits.addChild(portraitContainer);
    });
  }

  private createQRCode(cardWidth: number, cardHeight: number): void {
    const warriorIds = this.options.player.warriors.map(w => w.definitionId);
    const qrDataUrl = QRService.generateTeamQR(
      this.options.player.name,
      warriorIds,
      this.options.player.rating
    );

    // Create a label
    const label = new Text({
      text: 'Scan to load team',
      style: {
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.SM,
        fill: COLORS.TEXT_SECONDARY,
      },
    });
    label.anchor.set(0.5, 0);
    label.x = cardWidth / 2;
    label.y = 0;
    this.qrContainer.addChild(label);

    // QR code size (proportional to card dimensions)
    const qrSize = Math.min(cardWidth - SPACING.LG * 2, cardHeight * 0.35);

    // Load QR code using an Image element, then create texture
    const img = new Image();
    img.onload = () => {
      const texture = Texture.from(img);
      const qrSprite = new Sprite(texture);
      qrSprite.width = qrSize;
      qrSprite.height = qrSize;
      qrSprite.x = (cardWidth - qrSize) / 2; // Center horizontally
      qrSprite.y = SPACING.LG;
      this.qrContainer.addChild(qrSprite);
    };
    img.onerror = (err) => {
      console.warn('Failed to load QR code:', err);
      // Fallback: show placeholder
      const placeholder = new Graphics();
      placeholder.roundRect((cardWidth - qrSize) / 2, SPACING.LG, qrSize, qrSize, 4);
      placeholder.fill({ color: COLORS.TEXT_MUTED, alpha: 0.3 });
      this.qrContainer.addChild(placeholder);
    };
    img.src = qrDataUrl;
  }

  private createAIBadge(cardWidth: number, cardHeight: number): void {
    // Calculate proportional badge size
    const badgeWidth = Math.min(120, cardWidth * 0.4);
    const badgeHeight = Math.min(60, cardHeight * 0.12);

    // Badge background
    const badge = new Graphics();
    badge.roundRect((cardWidth - badgeWidth) / 2, SPACING.MD, badgeWidth, badgeHeight, 8);
    badge.fill({ color: COLORS.BG_TERTIARY });
    badge.stroke({ color: COLORS.TEXT_MUTED, width: 2 });
    this.qrContainer.addChild(badge);

    // AI text
    const aiText = new Text({
      text: 'AI',
      style: {
        fontFamily: FONTS.PRIMARY,
        fontSize: FONTS.SIZES.XXL,
        fontWeight: 'bold',
        fill: COLORS.TEXT_SECONDARY,
      },
    });
    aiText.anchor.set(0.5);
    aiText.x = cardWidth / 2;
    aiText.y = SPACING.MD + badgeHeight / 2;
    this.qrContainer.addChild(aiText);
  }

  /**
   * Update the card with new data
   */
  update(player: Player, ratingDelta: number): void {
    this.nameText.text = player.name;
    this.ratingText.text = player.rating.toString();

    const deltaColor = ratingDelta >= 0 ? COLORS.SUCCESS : COLORS.DANGER;
    const deltaPrefix = ratingDelta >= 0 ? '+' : '';
    this.deltaText.text = `(${deltaPrefix}${ratingDelta})`;
    this.deltaText.style.fill = deltaColor;
  }
}
